// Fixed simulation timing. Server and client step at the same rate so that
// one client input == one server tick, which keeps prediction/replay exact.
export const TICK_RATE = 60;
export const TICK_DT = 1 / TICK_RATE;
// Snapshots go out every N ticks (60 / 3 = 20 Hz).
export const SNAPSHOT_EVERY_TICKS = 3;
// What the netcore interpolator derives its windows from.
export const NET_TIMING = { tickRate: TICK_RATE, snapshotEveryTicks: SNAPSHOT_EVERY_TICKS } as const;
// Render delay for remote players (2 snapshot intervals = 100 ms); see docs/netcode.md.
export const INTERP_DELAY_TICKS = 2 * SNAPSHOT_EVERY_TICKS;

export const MAX_PLAYERS = 32;
// How many unprocessed inputs the server buffers per player before it starts
// consuming more than one per tick to catch up.
export const INPUT_QUEUE_SIZE = 16;
// The server consumes one input per tick; above this backlog it takes two,
// so a few queued inputs (network jitter) never become standing latency
// between the player's predicted position and the server's (was 8).
export const INPUT_CATCHUP_BACKLOG = 3;

// Level size comes from the loaded level (see map.ts); there is no fixed
// world size. Levels are usually larger than the screen on purpose: each
// player sees only the part of the map around them.

export const PLAYER_RADIUS = 14;
export const WALK_SPEED = 144; // px / s (was 180; both speeds -20% on 2026-10-03, felt too fast)
export const RUN_SPEED = 240; // px / s (was 300)
// Moving directly away from the aim is this fraction of normal speed, so
// backpedalling while shooting is not a free retreat. Sideways is unaffected.
export const BACKPEDAL_SPEED_FACTOR = 0.75;

// ---- combat (v1: one rifle, slow visible bullets) ------------------------
export const MAX_HEALTH = 100;
export const CLIP_SIZE = 30;
export const RESERVE_START = 90;
export const RELOAD_TICKS = 90; // 1.5 s
export const RESPAWN_TICKS = 180; // 3 s
export const SPAWN_PROTECT_TICKS = 180; // 3 s of invulnerability after (re)spawn
// Deathmatch: respawn at a random spot this far from your killer (px), so
// you come back into the fight. Falls back to spawn points if no free spot
// is found or the killer is gone.
export const RESPAWN_NEAR_MIN = 220;
export const RESPAWN_NEAR_MAX = 480;
export const RESPAWN_NEAR_TRIES = 24;
export const NO_HIT = 255; // "no player" in messages

// Fire is one shot per click (press edge); the cooldown only caps click spam.
export const RIFLE_COOLDOWN_TICKS = 6; // max 10 clicks / s

// ---- grenades (docs/health-and-damage.md "Grenades") ----------------------
// Switch model (Medal of Honor): the grenade is a weapon you hold. Switching
// takes WEAPON_SWITCH_TICKS during which nothing fires; with the grenade out
// the fire button throws it at the aimed distance; it flies over walls and
// bursts after GRENADE_FLIGHT_TICKS with damage falling off to the edge.
export const WEAPON_RIFLE = 0;
export const WEAPON_GRENADE = 1;
export const WEAPON_SWITCH_TICKS = 24; // 0.4 s each way
export const GRENADE_START = 2; // carried at (re)spawn
export const GRENADE_MAX = 4;
export const GRENADE_CRATE_AMOUNT = 1; // an ammo crate also gives this many
export const GRENADE_RANGE_MIN = 64; // px; no point-blank throws (a mode mistake, not a tactic)
export const GRENADE_RANGE_MAX = 320;
export const GRENADE_FLIGHT_TICKS = 42; // 0.7 s in the air, any distance
export const GRENADE_RADIUS = 70; // px blast radius (to the body centre)
export const GRENADE_DAMAGE_MAX = 100; // at the centre
export const GRENADE_DAMAGE_MIN = 25; // at the edge
export const GRENADE_COOLDOWN_TICKS = 60; // between throws
export const PROJ_BULLET = 0;
export const PROJ_GRENADE = 1;

// Bullets are projectiles, Commando style: visible, dodgeable, limited range.
export const BULLET_SPEED = 400; // px / s (a running soldier does 240)
export const BULLET_RANGE = 560; // px
export const BULLET_RADIUS = 4;
export const MAX_PROJECTILES = 128; // pool size per world

// Damage by where the bullet's centre crosses the body, as a fraction of
// PLAYER_RADIUS from the player's centre.
export const HIT_CENTER_FRAC = 0.35;
export const HIT_MID_FRAC = 0.7;
export const DAMAGE_CENTER = 50;
export const DAMAGE_MID = 25;
export const DAMAGE_EDGE = 10;

// Accuracy: max angular spread (radians) by movement state.
export const SPREAD_STAND = 0.009; // ~0.5 deg
export const SPREAD_WALK = 0.044; // ~2.5 deg
export const SPREAD_RUN = 0.122; // ~7 deg

// Kept for future hitscan weapons (see combat.ts raycast helpers).
export const HITSCAN_RANGE = 1200;

// ---- pickups -----------------------------------------------------------
export const MAX_PICKUPS = 32; // pool size per world
export const PICKUP_RADIUS = 12; // px, for touch and bullet tests
export const PICKUP_HEALTH = 1;
export const PICKUP_AMMO = 2;
export const RESERVE_MAX = 180; // ammo pickups can't push the reserve past this
// Dropped where a player dies: a health kit and an ammo crate, side by side.
export const DROP_HEALTH_AMOUNT = 25;
export const DROP_AMMO_AMOUNT = 30;
export const DROP_OFFSET = 14; // px either side of the death position (grenade pouch below)
export const DROP_TTL_TICKS = 30 * 60; // 30 s
// Death drops scatter: each kit slides out from the body in its own
// direction (2026-10-09) and is shielded from bullets for a moment so the
// killer's follow-up shots do not vaporise the loot.
export const DROP_SCATTER_SPEED_MIN = 150; // px/s at the start of the slide
export const DROP_SCATTER_SPEED_MAX = 260;
export const DROP_SLIDE_FRICTION = 0.86; // per tick; the slide lasts ~0.4 s
export const DROP_SHIELD_TICKS = 120; // 2 s: bullets pass over fresh drops
// Random health kits the server sprinkles on free floor tiles.
export const RANDOM_HEALTH_AMOUNT = 50;
export const RANDOM_HEALTH_TTL_TICKS = 60 * 60; // 60 s
export const RANDOM_HEALTH_MIN_S = 15;
export const RANDOM_HEALTH_MAX_S = 40;
export const RANDOM_HEALTH_MAX_ACTIVE = 4;
// Random ammo crates, same idea (added 2026-10-03: reserve ran dry too often).
export const RANDOM_AMMO_AMOUNT = 30;
export const RANDOM_AMMO_TTL_TICKS = 60 * 60; // 60 s
export const RANDOM_AMMO_MIN_S = 20;
export const RANDOM_AMMO_MAX_S = 50;
export const RANDOM_AMMO_MAX_ACTIVE = 3;
// Grenade crates (2026-10-09): a third as often as ammo, one at a time.
// A killed player's grenades drop with the body (PICKUP_GRENADE, amount =
// what they carried).
export const PICKUP_GRENADE = 4;
export const RANDOM_GRENADE_AMOUNT = 2;
export const RANDOM_GRENADE_TTL_TICKS = 60 * 60; // 60 s
export const RANDOM_GRENADE_MIN_S = 60;
export const RANDOM_GRENADE_MAX_S = 150;
export const RANDOM_GRENADE_MAX_ACTIVE = 1;

// ---- camouflage --------------------------------------------------------
// A rare pickup grants a charge; while charged and standing still the player
// looks like a prop to everyone else. Moving or shooting shows the soldier.
export const PICKUP_CAMO = 3;
export const CAMO_TICKS = 30 * 60; // charge length, 30 s
export const CAMO_REVEAL_TICKS = 60; // shooting shows the soldier for 1 s
export const CAMO_WARN_TICKS = 5 * 60; // HUD countdown for the last 5 s
export const CAMO_SPAWN_MIN_S = 20; // was 60-120 / 1 active: too rare to matter (2026-10-03)
export const CAMO_SPAWN_MAX_S = 45;
export const CAMO_MAX_ACTIVE = 2;
export const CAMO_TTL_TICKS = 90 * 60;
// Props a camouflaged player can look like.
export const PROP_NONE = 0;
export const PROP_BRICK = 1;
export const PROP_CRATE = 2;
export const PROP_HEALTH = 3;
export const PROP_AMMO = 4;
export const PROP_COUNT = 4;

// ---- visibility ----------------------------------------------------------
// Interest management: a client is only told about players, pickups and
// shots within VIEW_RADIUS px of its own soldier (server-side, so a
// modified client cannot see further). The client draws a fog ring from
// VIEW_RADIUS - VIEW_FADE_PX outward so the cut-off reads as distance, not
// as a bug, and zooming out never reveals more than this.
export const VIEW_RADIUS = 1000;
export const VIEW_FADE_PX = 160;

// ---- rooms ---------------------------------------------------------------
// One process hosts many rooms. Newcomers without a room id get "quick
// play": the fullest public room with space below the soft cap, else a new
// room. A room that stays empty is retired; the main room never is.
export const ROOM_ID_LEN = 6; // base32 chars in invite links
export const ROOM_NAME_MAX = 24;
export const ROOM_MAX_PLAYERS_DEFAULT = 20; // what one arena01 holds comfortably
export const ROOM_SOFT_CAP = 16; // quick play stops filling a room here
export const ROOM_EMPTY_TTL_S = 60;
export const MAIN_ROOM_ID = 'main';

// ---- game modes --------------------------------------------------------
export const MODE_DEATHMATCH = 0;
export const MODE_TEAM = 1;
export const MAX_TEAMS = 4; // design rule: never hard-code "two teams"
export const TEAM_COUNT_DEFAULT = 2;
export const TEAM_SIZE_DEFAULT = 3; // 3v3 is the friends format
export const NO_TEAM = 0;

// ---- match -------------------------------------------------------------
// A deathmatch ends at the kill limit or the time limit, whichever comes
// first, then everyone looks at the final board for an intermission and a
// fresh match starts. Defaults; rooms will override them (TODO(rooms)).
export const MATCH_KILL_LIMIT = 20;
export const MATCH_TIME_LIMIT_S = 5 * 60; // was 10 min until 2026-10-04
export const MATCH_INTERMISSION_S = 10;
export const MATCH_PLAYING = 0;
export const MATCH_INTERMISSION = 1;
// Team rooms start in a warm-up: everyone can play, nothing counts, the
// match starts when the room's human slots fill, when a player presses
// Start, or after MATCH_WARMUP_S (docs/rooms.md "Warm-up").
export const MATCH_WARMUP = 2;
export const MATCH_WARMUP_S = 45;
export const WARMUP_EXTEND_S = 15; // a late joiner gets at least this long before the start
// Quick play does not drop a newcomer into a match about to end.
export const BACKFILL_MIN_LEFT_S = 60;
export const NO_WINNER = 255; // draw, or nobody scored
